import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  useActiveQuests,
  useCompleteQuestObjective,
  usePitchInQuest,
  useArchiveQuest,
  newIdempotencyKey,
  resolveErrorMessage,
  QuestObjectiveResponseSourceKind,
  type QuestResponse,
  type QuestObjectiveResponse,
  type QuestContributorResponse,
} from '@kidshome/api-contract';
import { formatPoints, formatDay } from '@kidshome/i18n';
import { Button, Card, cn } from '@kidshome/ui';
import { ProgressBar, LoadingState, Avatar, useToast } from '@/shared/ui';
import { useHouseholdContext } from '@/shared/lib/household';
import { useCan, useFeature } from '@/entities/session';
import { Perm } from '@/shared/lib/permission-codes';
import { Feature } from '@/shared/lib/features';
import { tDynamic } from '@/shared/lib/i18n';
import { parseLocalDate, localDateISO } from '@/shared/lib/dates';
import { questTypeGlyph } from '@/shared/lib/quest-types';
import { ConceptIcon } from '@/shared/ui/ConceptIcon';
import { Illustration } from '@/shared/ui/Illustration';
import { questArt, conceptArt } from '@/assets/icons';
import { useMembersGuarded } from '@/entities/member/use-members-guarded';

/**
 * Cooperative family quest card (ADR-0029). The SHARED, non-competitive goal the whole household fills
 * together: one warm shared progress bar, the IRL reward note the family earns, and the "everyone
 * helped" contributors row — rendered in a STABLE order (never sorted-by-most), framing participation,
 * not competition. EVERYONE in the household (kids + guardians) reads the SAME card via
 * `quest:read`; the bar moves as kids complete chores/habits (the complete/check-in features
 * invalidate `['quests','active']`).
 *
 * States:
 *   - completed → a gentle one-shot celebration + the reward note (reduced-motion-safe; no confetti
 *     storm — a calm 🎉). The backend keeps a just-completed quest in the `active` read for a short
 *     celebration window, so this state IS reachable on the shared card.
 *   - expired-unmet (active quest past its end day) → a calm "this one wrapped up — try another!" with
 *     NO shaming, never a countdown/urgency.
 *   - active → shared bar + warm "Together you've done N of M!" + a kind "ends {{date}}" (no timer).
 *   - empty (no active quest) → a kind empty state: a guardian gets a "Start a family quest" CTA; a
 *     kid gets encouraging copy (they never manage the shared goal).
 *
 * A guardian (`quest:manage`) also gets the "Start a family quest" link on the completed/wrapped-up
 * card — the copy invites "try another!", so the affordance to do exactly that must be one tap away
 * (otherwise the wrapped-up card is a dead end until they independently find /admin/quests).
 *
 * Renders a LoadingState while the read is in flight and nothing (a thin invisible node) on error so a
 * transient quest-read failure never breaks the dashboard/day (the card is a secondary surface). Also
 * renders nothing when the household has `Feature.Quests` off — mirrors {@link KidQuestCard} — since
 * the API 403s the whole quests route for such a household, so the query is never even fired.
 */
export function FamilyQuestCard(): React.ReactElement | null {
  const { t } = useTranslation('family');
  const { locale, timeZone } = useHouseholdContext();
  const { can } = useCan();
  const hasFeature = useFeature();
  const questsEnabled = hasFeature(Feature.Quests);
  const quests = useActiveQuests({ enabled: questsEnabled });

  const canManage = can(Perm.QuestManage);

  if (!questsEnabled) return null;
  if (quests.isPending) return <LoadingState />;
  // A transient quest-read failure must not break the dashboard/day — the card is a secondary surface.
  if (quests.isError) return null;

  const activeQuests = quests.data?.quests ?? [];

  if (activeQuests.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-2 bg-surface-muted p-5 text-center">
        <Illustration name="empty.quests" fallbackGlyph="🧭" size={56} />
        <p className="font-display text-md font-bold text-text">{t('quest.empty.title')}</p>
        <p className="text-sm text-text-muted">
          {canManage ? t('quest.empty.guardianBody') : t('quest.empty.kidBody')}
        </p>
        {canManage && (
          <Button asChild size="sm" className="mt-1">
            <Link to="/admin/quests">{t('quest.empty.cta')}</Link>
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {activeQuests.map((quest) => (
        <ActiveFamilyQuest
          key={quest.id}
          quest={quest}
          locale={locale}
          timeZone={timeZone}
          canManage={canManage}
          t={t}
        />
      ))}
    </div>
  );
}

/** Progress bar with milestone tick marks at 25%, 50%, 75%. Not exported. */
function QuestProgressBar({
  percent,
  label,
}: {
  percent: number;
  label: string;
}): React.ReactElement {
  const MILESTONES = [25, 50, 75];
  return (
    <div className="flex flex-col gap-1">
      <ProgressBar percent={percent} label={label} />
      <div className="flex justify-around px-0" aria-hidden="true">
        {MILESTONES.map((m) => (
          <span
            key={m}
            className={cn(
              'size-2.5 rounded-full border-2 transition-colors',
              percent >= m ? 'border-primary bg-primary' : 'border-border bg-transparent',
            )}
          />
        ))}
      </div>
    </div>
  );
}

/** True when an active quest has an end day strictly before today's household-local civil day. */
function isExpired(quest: QuestResponse, timeZone: string): boolean {
  if (quest.status !== 'active' || !quest.endsAt) return false;
  return quest.endsAt < localDateISO(timeZone);
}

function ActiveFamilyQuest({
  quest,
  locale,
  timeZone,
  canManage,
  t,
}: {
  quest: QuestResponse;
  locale: string;
  timeZone: string;
  canManage: boolean;
  t: ReturnType<typeof useTranslation<'family'>>['t'];
}): React.ReactElement {
  const completed = quest.status === 'completed';
  const expired = isExpired(quest, timeZone);

  // A completed quest gets a real celebratory moment for EVERYONE (ADR-0054 #7), not a status line:
  // the quest name, "We did it — together!", the rewardNote front-and-center, the "everyone helped" row
  // given prominence, and — for a guardian — "Mark the reward given" (archives) + "Start a new quest".
  if (completed) {
    return <CompletedQuestCelebration quest={quest} canManage={canManage} t={t} />;
  }

  return (
    <Card className="flex flex-col gap-3 bg-surface-muted p-4">
      <div className="flex items-center gap-3">
        <span
          className="flex size-11 items-center justify-center rounded-md bg-accent"
          aria-hidden="true"
        >
          <ConceptIcon
            art={questArt(quest.questType)}
            glyph={questTypeGlyph(quest.questType)}
            size={28}
          />
        </span>
        <div className="min-w-0 flex-1">
          {/* Quest title is RAW user content — rendered verbatim, never translated. */}
          <p className="truncate font-bold text-text">{quest.title}</p>
          <p className="text-xs font-semibold text-text-muted">{t('quest.subtitle')}</p>
        </div>
        <span className="font-display text-lg font-bold text-primary">
          {Math.round(quest.percent)}%
        </span>
      </div>

      {/* Optional quest description — RAW user content, verbatim. */}
      {quest.description && <p className="text-sm text-text-muted">{quest.description}</p>}

      {/* `quest.percent` is the server-derived progress/targetCount*100 — reuse it (the header shows the
          same value) rather than recomputing and risking a round-disagreement. */}
      <QuestProgressBar percent={quest.percent} label={t('quest.progressLabel')} />

      {/* Per-objective mini-rows — each named sub-goal with its own calm bar (ADR-0032). A milestone
          objective shows a kid a gentle "a grown-up will check this off" hint, and a guardian a kind
          "Mark done" button. Never shaming; cooperative. */}
      {quest.objectives.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            {t('quest.goalsLabel')}
          </p>
          <ul className="flex flex-col gap-2.5">
            {quest.objectives.map((obj) => (
              <ObjectiveRow
                key={obj.id}
                questId={quest.id}
                objective={obj}
                locale={locale}
                canManage={canManage}
                t={t}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Status line: a calm "wrapped up" when an active quest passed its end day unmet (no shaming),
          otherwise the warm "Together you've done N of M!". (A completed quest renders the celebration
          card instead — handled above.) */}
      {expired ? (
        <p className="text-center text-sm font-medium text-text-muted">{t('quest.wrappedUp')}</p>
      ) : (
        <p className="text-center text-sm font-semibold text-text">
          {t('quest.together', {
            progress: formatPoints(locale, quest.progress),
            target: formatPoints(locale, quest.targetCount),
          })}
        </p>
      )}

      {/* "X more to go!" — shown only when active, not expired, and not yet complete. */}
      {!expired && quest.progress < quest.targetCount && (
        <p className="text-center text-xs font-medium text-text-muted">
          {t('quest.toGo', {
            remaining: formatPoints(locale, Math.max(0, quest.targetCount - quest.progress)),
          })}
        </p>
      )}

      {/* The IRL celebration the family honors — RAW user content. */}
      {quest.rewardNote && (
        <p className="text-center text-sm text-text-muted">
          <span aria-hidden="true">{'→ '}</span>
          <span className="font-semibold text-text">{quest.rewardNote}</span>
        </p>
      )}

      {/* Kind deadline — a calm "ends {{date}}", NEVER a countdown/urgency. `parseLocalDate` anchors
          `endsAt` at UTC noon, so it must be read back with 'UTC' (not the household's real timeZone)
          or a UTC+12-or-later household sees a date one day past the real deadline. */}
      {!expired && quest.endsAt && (
        <p className="text-center text-xs text-text-muted">
          {t('quest.endsOn', {
            date: formatDay(locale, parseLocalDate(quest.endsAt), 'UTC'),
          })}
        </p>
      )}

      {/* Once an active quest has wrapped up unmet, a guardian gets the "start another" path right here
          — the wrapped-up copy invites "try another!", so the affordance must not be a dead end. Kids
          never see it (they don't manage the shared goal). */}
      {canManage && expired && (
        <Button asChild size="sm" variant="ghost" className="self-center">
          <Link to="/admin/quests">{t('quest.empty.cta')}</Link>
        </Button>
      )}

      <Contributors contributors={quest.contributors} t={t} />
    </Card>
  );
}

/**
 * The completion celebration (ADR-0054 #7) — a real shared moment for EVERYONE when a quest fills, not
 * a one-line status. Shows a calm 🎉, the quest name, "We did it — together!", and the parent's
 * `rewardNote` front-and-center as the promised real-world reward, with the cooperative "everyone
 * helped" row given prominence (parents appear here too via pitch-in, ADR-0049). The entrance uses the
 * `animate-in fade-in` pattern (tw-animate-css), which is calm-mode / reduced-motion safe — no looping
 * confetti, no FOMO.
 *
 * A GUARDIAN (`quest:manage`) additionally gets the next step so the card is never a dead end:
 *  - "Mark the reward given" → archives the quest (acknowledge the IRL reward was honored + wrap up),
 *  - "Start a new quest" → /admin/quests.
 * A kid sees only the celebration + rewardNote (they never manage the shared goal).
 */
function CompletedQuestCelebration({
  quest,
  canManage,
  t,
}: {
  quest: QuestResponse;
  canManage: boolean;
  t: ReturnType<typeof useTranslation<'family'>>['t'];
}): React.ReactElement {
  const toast = useToast();
  // A dedicated `t` bound to the `errors` namespace too (mirrors HeroSwitcher/PetCollection/etc.) — the
  // `t` prop above is scoped to `family` only, which can't resolve `resolveErrorMessage`'s `errors:*` keys.
  const { t: tErr } = useTranslation(['family', 'errors']);
  const archive = useArchiveQuest({
    onSuccess: () => toast.success(t('quest.celebrate.rewardGivenToast')),
    // Unlike QuestsPage's own archive flow, this card had NO onError at all — a failure (offline,
    // server error) left the tap looking like it silently did nothing. Surface it like every other
    // quest-mutation failure in this feature.
    onError: (error) => {
      const { key, params } = resolveErrorMessage(error);
      toast.error(tDynamic(tErr, key, params));
    },
  });

  return (
    <Card
      role="status"
      className="flex animate-in flex-col items-center gap-3 bg-accent/40 p-5 text-center fade-in duration-500"
    >
      <ConceptIcon art={conceptArt('celebrate')} glyph="🎉" size={44} />
      {/* Quest title is RAW user content — verbatim, never translated. */}
      <p className="font-display text-lg font-bold text-text">{quest.title}</p>
      <p className="text-md font-semibold text-text">{t('quest.celebrate.weDidIt')}</p>

      {/* The promised IRL reward, front-and-center (RAW user content — verbatim, no auto-payout). */}
      {quest.rewardNote && (
        <div className="flex w-full flex-col gap-1 rounded-md bg-surface p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            {t('quest.celebrate.rewardLabel')}
          </p>
          <p className="font-semibold text-text">{quest.rewardNote}</p>
        </div>
      )}

      {/* The cooperative "everyone helped" row — given prominence on the completed card. */}
      <Contributors contributors={quest.contributors} t={t} />

      {/* Guardian next step — never a dead end. A kid sees only the celebration above. */}
      {canManage && (
        <div className="flex flex-col gap-2 self-stretch pt-1 sm:flex-row sm:justify-center">
          <Button
            size="sm"
            disabled={archive.isPending}
            onClick={() =>
              archive.mutate({ questId: quest.id, idempotencyKey: newIdempotencyKey() })
            }
          >
            {t('quest.celebrate.markRewardGiven')}
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link to="/admin/quests">{t('quest.celebrate.startNew')}</Link>
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * One objective mini-row under the shared bar: the named sub-goal + a small progress bar + its
 * `progress/target` (or a ✓ when done). A `milestone` source has no auto-progress — it's a manual
 * finish-line a grown-up ticks: a KID sees a gentle "a grown-up will check this off" hint; a GUARDIAN
 * (`quest:manage`) sees a kind "Mark done" button wired to the complete-objective command. On success
 * the active card + history are invalidated so the bar reconciles. The mutation carries no body and the
 * route is idempotent, so an offline replay is safe (like the other quest writes). Never shaming.
 */
function ObjectiveRow({
  questId,
  objective,
  locale,
  canManage,
  t,
}: {
  questId: string;
  objective: QuestObjectiveResponse;
  locale: string;
  canManage: boolean;
  t: ReturnType<typeof useTranslation<'family'>>['t'];
}): React.ReactElement {
  const toast = useToast();
  // Both routes are `@Idempotent` — the wrapper hooks carry an `Idempotency-Key` (without one the server
  // 400s and the tap silently does nothing) and invalidate the quest views on success.
  const { t: tErr } = useTranslation(['family', 'errors']);
  const complete = useCompleteQuestObjective({
    onSuccess: () => toast.success(t('quest.objective.markedDone')),
    onError: (error) => {
      const { key, params } = resolveErrorMessage(error);
      toast.error(tDynamic(tErr, key, params));
    },
  });
  // "Everyone pitches in": ANY member (kid or grown-up) taps to add +1 to a `together` objective, so a
  // parent participates in the shared goal alongside the kids (ADR-0049).
  const pitch = usePitchInQuest({
    onSuccess: () => toast.success(t('quest.objective.pitchedIn')),
    onError: (error) => {
      const { key, params } = resolveErrorMessage(error);
      toast.error(tDynamic(tErr, key, params));
    },
  });

  const pct = objective.targetCount > 0 ? (objective.progress / objective.targetCount) * 100 : 0;
  const isMilestone = objective.sourceKind === QuestObjectiveResponseSourceKind.milestone;
  const isTogether = objective.sourceKind === QuestObjectiveResponseSourceKind.together;
  const showTick = isMilestone && !objective.done && canManage;
  const showKidHint = isMilestone && !objective.done && !canManage;
  // A `together` objective offers a "pitch in" tap to EVERYONE (the whole family helps), not just guardians.
  const showPitchIn = isTogether && !objective.done;

  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        {/* Objective label is RAW user content — verbatim, never translated. */}
        <span className="min-w-0 truncate text-sm font-medium text-text">{objective.label}</span>
        <span className="whitespace-nowrap text-xs font-medium text-text-muted">
          {objective.done ? (
            <span className="text-success">
              <span aria-hidden="true">{'✓ '}</span>
              {t('quest.objective.doneLabel')}
            </span>
          ) : (
            t('quest.objective.progress', {
              progress: formatPoints(locale, objective.progress),
              target: formatPoints(locale, objective.targetCount),
            })
          )}
        </span>
      </div>
      <ProgressBar percent={pct} label={t('quest.objective.barLabel')} />
      {showKidHint && (
        <p className="text-xs text-text-muted">
          <span aria-hidden="true">{'🌟 '}</span>
          {t('quest.objective.milestoneKidHint')}
        </p>
      )}
      {showTick && (
        <Button
          size="sm"
          // `outline` (border + surface fill), not `ghost` — a ghost button is borderless/fill-less
          // and reads as plain bold text until hovered, so the affordance was invisible. This matches
          // the sibling "pitch in" pill so both objective actions look like the tappable controls they are.
          variant="outline"
          className="self-start"
          disabled={complete.isPending}
          onClick={() =>
            complete.mutate({
              questId,
              objectiveId: objective.id,
              idempotencyKey: newIdempotencyKey(),
            })
          }
        >
          {t('quest.objective.markDone')}
        </Button>
      )}
      {showPitchIn && (
        <Button
          size="sm"
          variant="outline"
          className="self-start"
          disabled={pitch.isPending}
          onClick={() =>
            pitch.mutate({
              questId,
              objectiveId: objective.id,
              idempotencyKey: newIdempotencyKey(),
            })
          }
        >
          <span aria-hidden="true">{'🙌 '}</span>
          {t('quest.objective.pitchIn')}
        </Button>
      )}
    </li>
  );
}

/**
 * The cooperative "everyone helped" row. Rendered in the STABLE household order the API returns —
 * explicitly NOT sorted-by-most: this is participation framing, not a leaderboard. Each child shows
 * their avatar + a count of contributions (kind, not a rank). Renders nothing if nobody has helped yet
 * (the bar + "together" copy already carry the cooperative message), and likewise renders nothing if
 * the viewer's role lacks `member:read` (ADR-0137) — the quest bar/progress itself still renders in the
 * parent card either way; this row alone has no honest avatar/name to show without the roster.
 */
function Contributors({
  contributors,
  t,
}: {
  contributors: QuestContributorResponse[];
  t: ReturnType<typeof useTranslation<'family'>>['t'];
}): React.ReactElement | null {
  // Resolve each contributor's avatar from the (already-cached) members read; the quest response
  // carries only the childId + display name + count, not an avatar key. Falls back to the default
  // glyph while the read is in flight.
  const members = useMembersGuarded();
  const avatarById = new Map((members.data?.data ?? []).map((m) => [m.id, m.avatar]));

  if (members.forbidden || contributors.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('quest.helpedTitle')}
      </p>
      <ul className="flex flex-wrap gap-2">
        {contributors.map((c) => (
          <li
            key={c.childId}
            className="flex min-w-0 max-w-full items-center gap-1.5 rounded-pill bg-surface px-2 py-1"
          >
            <Avatar avatar={avatarById.get(c.childId) ?? null} size="sm" />
            {/* Display name is RAW user content — verbatim. `truncate` (+ the parent's `min-w-0`) clips
                a very long name gracefully instead of letting it break the wrap layout. */}
            <span className="truncate text-sm font-semibold text-text">{c.displayName}</span>
            <span className="text-xs font-medium text-text-muted">
              {t('quest.helpedCount', { count: c.count })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
